import React, { useState, useRef, useEffect } from 'react';

// API base URL for Cloudflare Pages Functions
const API_BASE_URL = process.env.NODE_ENV === 'production' ? '/api' : '/api';

function App() {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedModel, setSelectedModel] = useState('gpt-5');
  const [availableModels, setAvailableModels] = useState([]);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchModels();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchModels = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/models`);
      const data = await response.json();
      setAvailableModels(data);
    } catch (error) {
      console.error('Failed to fetch models:', error);
      // Fallback models
      setAvailableModels([
        { id: 'gpt-5', name: 'GPT-5', provider: 'OpenAI' },
        { id: 'gemini-2.5-flash', name: 'Gemini 2.5 Flash', provider: 'Google' }
      ]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = { 
      role: 'user', 
      content: inputMessage, 
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: inputMessage,
          model: selectedModel
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      const assistantMessage = { 
        role: 'assistant', 
        content: data.response,
        model: data.model,
        timestamp: data.timestamp,
        processingTime: data.processingTime
      };
      
      setMessages(prev => [...prev, assistantMessage]);

    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = { 
        role: 'assistant', 
        content: `錯誤：${error.message}`,
        isError: true,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const clearChat = () => {
    setMessages([]);
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('zh-TW', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="app">
      <header className="app-header">
        <h1>🚀 LLM簡單聊天</h1>
        <div className="architecture-badge">
          <span>☁️ Cloudflare Pages Full-Stack</span>
        </div>
        <div className="header-controls">
          <div className="model-selector">
            <label htmlFor="model-select">選擇模型：</label>
            <select 
              id="model-select"
              value={selectedModel} 
              onChange={(e) => setSelectedModel(e.target.value)}
              disabled={isLoading}
            >
              {availableModels.map(model => (
                <option key={model.id} value={model.id} title={model.description}>
                  {model.name} ({model.provider})
                </option>
              ))}
            </select>
          </div>
          <button onClick={clearChat} className="clear-button" disabled={isLoading}>
            清除對話
          </button>
        </div>
      </header>

      <main className="chat-container">
        <div className="messages">
          {messages.length === 0 && (
            <div className="welcome-message">
              <h2>歡迎使用 LLM簡單聊天！</h2>
              <p>選擇一個 AI 模型並開始對話吧。</p>
              <div className="model-info">
                <p>🤖 <strong>GPT-5</strong>: OpenAI 的先進語言模型</p>
                <p>⚡ <strong>Gemini 2.5 Flash</strong>: Google 的快速智能模型</p>
              </div>
            </div>
          )}
          
          {messages.map((message, index) => (
            <div 
              key={index} 
              className={`message ${message.role} ${message.isError ? 'error' : ''}`}
            >
              <div className="message-header">
                <span className="role">
                  {message.role === 'user' ? '👤 您' : '🤖 AI'}
                  {message.model && ` (${message.model})`}
                </span>
                <span className="timestamp">
                  {formatTimestamp(message.timestamp)}
                  {message.processingTime && (
                    <span className="processing-time"> • {message.processingTime}ms</span>
                  )}
                </span>
              </div>
              <div className="message-content">
                {message.content}
              </div>
            </div>
          ))}
          
          {isLoading && (
            <div className="message assistant loading">
              <div className="message-header">
                <span className="role">🤖 AI ({selectedModel})</span>
                <span className="timestamp">思考中...</span>
              </div>
              <div className="message-content">
                <div className="typing-indicator">
                  <span></span><span></span><span></span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        <form onSubmit={handleSubmit} className="input-form">
          <div className="input-container">
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder={`向 ${availableModels.find(m => m.id === selectedModel)?.name || selectedModel} 提問...`}
              disabled={isLoading}
              className="message-input"
            />
            <button 
              type="submit" 
              disabled={!inputMessage.trim() || isLoading}
              className="send-button"
            >
              {isLoading ? '🤔' : '📤'}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}

export default App;